import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Login from './pages/Login';
import Sessions from './pages/Sessions';
import Booking from './pages/Booking';
import MyBookings from './pages/MyBookings';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogs from './pages/AdminLogs';
import AdminSMS from './pages/AdminSMS';
import Gusmasters from './pages/Gusmasters';
import './App.css';

// Configure axios defaults
axios.defaults.withCredentials = true;

interface User {
  id: number;
  name: string;
  email: string;
  isAdmin: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (user: User) => void;
  logout: () => void;
  loading: boolean;
}

export const AuthContext = React.createContext<AuthContextType>({
  user: null,
  login: () => {},
  logout: () => {},
  loading: true,
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/user');
      setUser({ ...response.data, isAdmin: !!response.data.isAdmin });
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = (userData: User) => {
    setUser({ ...userData, isAdmin: !!userData.isAdmin });
  };

  const logout = async () => {
    try {
      await axios.post('/api/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Navbar />
          <main className="container mx-auto px-4 py-8">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/sessions" element={<Sessions />} />
              <Route 
                path="/gusmasters" 
                element={user ? <Gusmasters /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/booking/:sessionId" 
                element={user ? <Booking /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/my-bookings" 
                element={user ? <MyBookings /> : <Navigate to="/login" />} 
              />
              <Route 
                path="/admin" 
                element={user?.isAdmin ? <AdminDashboard /> : <Navigate to="/" />} 
              />
              <Route 
                path="/admin/logs" 
                element={user?.isAdmin ? <AdminLogs /> : <Navigate to="/" />} 
              />
              <Route 
                path="/admin/sms" 
                element={user?.isAdmin ? <AdminSMS /> : <Navigate to="/" />} 
              />
            </Routes>
          </main>
        </div>
      </Router>
    </AuthContext.Provider>
  );
}

export default App; 